/**
 * Cloud Agent (super-admin) — conversation state.
 *
 * The page is a chat: a transcript of turns plus the cost/usage accounting for the
 * conversation. All of it is cross-component (transcript, input, cost widget, scope
 * picker), which is why it lives in a store rather than in `CloudAgentPage` state.
 *
 * Nothing here is persisted — the server keeps conversations in memory with a TTL, so a
 * reload starts fresh on both sides.
 */
import { create } from 'zustand';
import {
  askStream,
  deleteConversation,
  CloudAgentRequestError,
  type AskStreamHandlers,
  type AskStreamMeta,
} from '../services/admin/cloudAgentService';
import type {
  AnswerDepth,
  CloudAgentScope,
  ConversationUsage,
  HydratedBlock,
  HydratedSpec,
  UsageStat,
} from '../services/admin/cloudAgentTypes';

/** Where the page is in the ask → stream → answer cycle. */
export type CloudAgentPhase = 'idle' | 'streaming' | 'error';

/** One entry in the transcript. A user question, or one assistant turn. */
export interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  /** User: the question. Assistant: streamed prose, replaced by the final `TurnAnswer.text`. */
  text: string;
  /** Assistant only: blocks in arrival order (also the streaming render). */
  blocks: HydratedBlock[];
  /** Assistant only: the hydrated spec, kept so a `filter` block can re-hydrate this turn. */
  spec: HydratedSpec | null;
  /** Assistant only: how much UI the agent chose. */
  depth: AnswerDepth | null;
  /** Assistant only: progress lines received while tools ran. */
  status: string[];
  /** Assistant only: true until `done` (or an error) lands. */
  streaming: boolean;
  /** When the turn started, and when it stopped (null while running) — drives the "· 12s". */
  startedAt: number;
  endedAt: number | null;
  /**
   * Assistant only: null unless the turn failed. `reason` is the RAW server/transport text,
   * null when there is none — the store never invents user-facing copy, so the component
   * frames it with a translated string and appends the reason only if present.
   */
  error: { reason: string | null } | null;
}

interface CloudAgentState {
  scope: CloudAgentScope | null;
  /** Human label for the active scope (restaurant name, or the all-restaurants label). */
  scopeLabel: string;
  conversationId: string | null;
  model: string | null;
  turns: ChatTurn[];
  phase: CloudAgentPhase;
  /** Per-turn usage rows, in turn order (the cost widget popover). */
  usageTurns: UsageStat[];
  /** Running conversation total, straight from the server's `usage` frame. */
  usageConversation: ConversationUsage | null;

  setScope: (scope: CloudAgentScope, label: string) => void;
  send: (question: string) => Promise<void>;
  /** Replace one assistant turn's spec + blocks after a filter re-hydration. */
  replaceSpec: (turnId: string, spec: HydratedSpec) => void;
  /** Clear the transcript and drop the server-side conversation (best-effort). Keeps the scope. */
  newConversation: () => void;
  /** Abort any in-flight stream (page unmount). Leaves the transcript intact. */
  cancel: () => void;
}

/**
 * Server error codes meaning "the conversationId you sent is no longer usable". Both are
 * returned before any SSE frame, so the turn can be retried cleanly as a new conversation.
 */
const STALE_CONVERSATION_CODES = new Set(['scope_mismatch', 'conversation_not_found']);

/** Not state: aborting must not re-render, and the controller is not serialisable. */
let activeController: AbortController | null = null;
let turnSeq = 0;

const nextId = (role: string) => `${role}-${Date.now()}-${++turnSeq}`;

/** Apply `patch` to the turn with `id`, leaving every other turn untouched. */
function patchTurn(turns: ChatTurn[], id: string, patch: Partial<ChatTurn>): ChatTurn[] {
  return turns.map((turn) => (turn.id === id ? { ...turn, ...patch } : turn));
}

export const useAppCloudAgentStore = create<CloudAgentState>((set, get) => ({
  scope: null,
  scopeLabel: '',
  conversationId: null,
  model: null,
  turns: [],
  phase: 'idle',
  usageTurns: [],
  usageConversation: null,

  setScope: (scope, label) => set({ scope, scopeLabel: label }),

  send: async (question) => {
    const trimmed = question.trim();
    const { scope, conversationId, phase } = get();
    if (!trimmed || !scope || phase === 'streaming') return;

    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;

    const assistantId = nextId('assistant');
    set((state) => ({
      phase: 'streaming',
      turns: [
        ...state.turns,
        {
          id: nextId('user'),
          role: 'user',
          text: trimmed,
          blocks: [],
          spec: null,
          depth: null,
          status: [],
          streaming: false,
          error: null,
          startedAt: Date.now(),
          endedAt: Date.now(),
        },
        {
          id: assistantId,
          role: 'assistant',
          text: '',
          blocks: [],
          spec: null,
          depth: null,
          status: [],
          streaming: true,
          error: null,
          startedAt: Date.now(),
          endedAt: null,
        },
      ],
    }));

    /** A superseded / aborted stream must never write into the transcript. */
    const isCurrent = () => activeController === controller && !controller.signal.aborted;

    const handlers: AskStreamHandlers = {
          onMeta: (meta: AskStreamMeta) => {
            if (!isCurrent()) return;
            set({ conversationId: meta.conversationId, model: meta.model });
          },
          onStatus: (message) => {
            if (!isCurrent()) return;
            set((state) => ({
              turns: state.turns.map((turn) =>
                turn.id === assistantId ? { ...turn, status: [...turn.status, message] } : turn
              ),
            }));
          },
          onText: (delta) => {
            if (!isCurrent() || !delta) return;
            set((state) => ({
              turns: state.turns.map((turn) =>
                turn.id === assistantId ? { ...turn, text: turn.text + delta } : turn
              ),
            }));
          },
          onBlock: (block) => {
            if (!isCurrent()) return;
            set((state) => ({
              turns: state.turns.map((turn) =>
                turn.id === assistantId ? { ...turn, blocks: [...turn.blocks, block] } : turn
              ),
            }));
          },
          onDone: (answer) => {
            if (!isCurrent()) return;
            set((state) => ({
              phase: 'idle',
              turns: state.turns.map((turn) =>
                turn.id === assistantId
                  ? {
                      ...turn,
                      // The final prose is authoritative; the streamed deltas can include
                      // the model's intermediate tool-loop chatter.
                      text: answer.text || turn.text,
                      spec: answer.spec,
                      blocks: answer.spec ? answer.spec.blocks : turn.blocks,
                      depth: answer.depth,
                      streaming: false,
                      endedAt: Date.now(),
                    }
                  : turn
              ),
            }));
          },
      onUsage: (turn, conversation) => {
        if (!isCurrent()) return;
        set((state) => ({
          usageTurns: [...state.usageTurns, turn],
          usageConversation: conversation,
        }));
      },
    };

    const attempt = (convId: string | null) =>
      askStream(
        { conversationId: convId ?? undefined, scope, question: trimmed },
        handlers,
        controller.signal
      );

    try {
      try {
        await attempt(conversationId);
      } catch (err) {
        // The server forgot this conversation (TTL / eviction) or refuses the scope on it.
        // Both are answered before any frame is streamed, so one clean retry as a brand-new
        // conversation recovers the turn instead of surfacing a dead end to the super-admin.
        const isStale =
          !!conversationId &&
          err instanceof CloudAgentRequestError &&
          !!err.code &&
          STALE_CONVERSATION_CODES.has(err.code);
        if (!isStale || !isCurrent()) throw err;

        console.error('[CloudAgent] Conversation was stale, retrying as a new one:', err);
        set((state) => ({
          conversationId: null,
          model: null,
          // The server's conversation total restarts at zero, so the per-turn rows must too.
          usageTurns: [],
          usageConversation: null,
          turns: patchTurn(state.turns, assistantId, { text: '', blocks: [], status: [] }),
        }));
        await attempt(null);
      }
      // A stream that ends without `done` (server closed early) must not leave a spinner.
      if (isCurrent() && get().phase === 'streaming') {
        set((state) => ({
          phase: 'idle',
          turns: patchTurn(state.turns, assistantId, { streaming: false, endedAt: Date.now() }),
        }));
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error('[CloudAgent] Turn failed:', err);
      set((state) => ({
        phase: 'error',
        turns: patchTurn(state.turns, assistantId, {
          streaming: false,
          endedAt: Date.now(),
          error: { reason: err instanceof Error ? err.message : null },
        }),
      }));
    } finally {
      if (activeController === controller) activeController = null;
    }
  },

  replaceSpec: (turnId, spec) =>
    set((state) => ({
      turns: patchTurn(state.turns, turnId, { spec, blocks: spec.blocks }),
    })),

  newConversation: () => {
    const { conversationId } = get();
    activeController?.abort();
    activeController = null;
    if (conversationId) void deleteConversation(conversationId);
    set({
      conversationId: null,
      model: null,
      turns: [],
      phase: 'idle',
      usageTurns: [],
      usageConversation: null,
    });
  },

  cancel: () => {
    activeController?.abort();
    activeController = null;
  },
}));
